import CheckIcon from '@mui/icons-material/Check';
import { Checkbox } from 'common/components/Checkbox/Checkbox';
import { Pool } from 'common/models';
import { useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import styled from 'styled-components';
import {
  CenteredHeader,
  CenteredText,
} from '../components/Sub-views/RatesView/Tabs/styles';

const SizedCenteredText = styled(CenteredText)`
  height: 24px;
`;

export type PoolTableRows = {
  poolId: string;
  poolDesc: string;
  active: { isActive: boolean; formValueKey: string };
};

export const usePoolsTableData: {
  (
    rows: Pool[],
    isSubmitting: boolean,
  ): {
    columns: Column<PoolTableRows>[];
    data: PoolTableRows[];
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
  };
} = (rows, isSubmitting) => {
  const [isEditing, setIsEditing] = useState(false);

  const buildActiveColumn: () => Column<PoolTableRows> = useCallback(
    () => ({
      Header: () => <CenteredHeader>Active</CenteredHeader>,
      accessor: 'active',
      width: '25%',
      Cell: ({ value }) => (
        <SizedCenteredText>
          {isEditing ? (
            <Checkbox
              formProps={{ name: value.formValueKey }}
              isDisabled={isSubmitting}
            />
          ) : (
            <>{value.isActive && <CheckIcon />}</>
          )}
        </SizedCenteredText>
      ),
    }),
    [isEditing, isSubmitting],
  );

  const columns: Column<PoolTableRows>[] = useMemo(
    () => [
      {
        Header: () => <CenteredHeader>Pool ID</CenteredHeader>,
        accessor: 'poolId',
        width: '25%',
        Cell: ({ value }) => <SizedCenteredText>{value}</SizedCenteredText>,
      },
      {
        Header: () => <CenteredHeader>Description</CenteredHeader>,
        accessor: 'poolDesc',
        width: '50%',
        Cell: ({ value }) => <SizedCenteredText>{value}</SizedCenteredText>,
      },
      buildActiveColumn(),
    ],
    [buildActiveColumn],
  );

  const data: PoolTableRows[] = useMemo(
    () =>
      rows.map(({ id, poolId, description, active }) => ({
        id,
        poolId,
        poolDesc: description,
        active: { isActive: active, formValueKey: id.toString() },
      })),
    [rows],
  );

  return {
    columns,
    data,
    isEditing,
    setIsEditing,
  };
};
